import { s3URL } from '@mathflat/common/index'
import type { ISODateTimeString } from '@mathflat/shared/@types/date'
import { addDays, format, isBefore } from 'date-fns'
import { action, computed, makeObservable, observable } from 'mobx'

import type { EXTRA_INFO } from '@/page/etc/InitSetting/ExtraInfo/ExtraInfo'
import commonRepo from '@/repository/common.repo'
import stringUtils from '@/utils/String/stringUtils'

import { DTO } from '../DTO'

export class Academy extends DTO {
  academyId?: res.Academy['academyId']
  status: res.Academy['status'] = 'FREE_INPROGRESS'
  name: res.Academy['name'] = ''
  email: res.Academy['email'] = ''
  logoUrl: res.Academy['logoUrl'] = ''
  initialized: res.Academy['initialized'] = false
  initializedDate: Date | null = null
  // 학원 대표 번호 (문자 인증 받은) 모든 선생님이 이 번호로만 문자 보낼 수 있음
  cellPhone: res.Academy['cellPhone'] = ''
  sellerCode: res.Academy['sellerCode'] = ''
  freeStartDate: res.Academy['freeStartDate'] = ''
  freeEndDate: res.Academy['freeEndDate'] = ''
  payDate?: res.Academy['payDate']
  payStartDate: res.Academy['payStartDate'] = ''
  payEndDate: res.Academy['payEndDate'] = ''
  freePremiumStartDate: res.Academy['freePremiumStartDate'] = ''
  freePremiumEndDate: res.Academy['freePremiumEndDate'] = ''
  onlineOnboardingStartedDate: res.Academy['onlineOnboardingStartedDate'] = null
  onlineOnboardingEndedDate: res.Academy['onlineOnboardingEndedDate'] = null
  logoOperation: res.Academy['logoOperation'] = 'REPLACE_DEFAULT'
  logoUrlDateNow? = new Date().getTime()
  worksheetShareSetting: res.Academy['worksheetShareSetting'] = {
    allWorksheetShareEnabled: false,
    allWorksheetShareDatetime: format(new Date(), 'yyyy-MM-ddTHH:mm:ss') as ISODateTimeString,
    details: {
      MY_DB: {
        enabled: false,
        datetime: format(new Date(), 'yyyy-MM-ddTHH:mm:ss') as ISODateTimeString,
      },
    },
  }
  supportedWorkbookTypes: res.Academy['supportedWorkbookTypes'] = [
    'CUSTOM',
    'PUBLIC',
    'SCHOOL',
    'ALL',
  ] // 지원교재 리스트 - [CUSTOM: 내 교재, PUBLIC: 시중교재, SCHOOL: 교과서, SCHOOL_TEST: 내신기출 교재, ALL] (필수)
  studentCount = 0
  lessonClassCount = 0
  serviceSubscriptions: res.Academy['serviceSubscriptions'] = []
  monthlyBaseFee: res.Academy['monthlyBaseFee'] = 0
  customerType: res.Academy['customerType'] = null

  address: res.Academy['address'] = ''
  addressDetail: res.Academy['addressDetail'] = ''
  zipCode: res.Academy['zipCode'] = ''
  metadata: Omit<
    res.Academy['metadata'],
    | 'smsService'
    | 'attendanceSmsService'
    | 'lectureService'
    | 'thirdPartyProvisionConsentOnPageShop'
    | 'oneClickWorksheetService'
    | 'teacherCount'
    | 'studentCount'
    | 'curriculums'
    | 'subjects'
  > & {
    smsService: boolean
    attendanceSmsService: boolean
    lectureService: boolean
    thirdPartyProvisionConsentOnPageShop: boolean
    oneClickWorksheetService: boolean
    studentCount: string
    teacherCount: string
    curriculums: (typeof EXTRA_INFO)['curriculum'][number]['id'][]
    subjects: string[]
  } = {
    attendanceSmsService: false,
    smsService: false,
    lectureService: false,
    thirdPartyProvisionConsentOnPageShop: false,
    oneClickWorksheetService: true,
    device: '',
    freeTrialCount: 0,
    popupTargetingGroup: '',
    freePremiumAvailable: false,
    academyApplicationDate: null,
    academyExpectedCompletionDate: null,
    academyTrainingBCompleted: false,
    academyFinalCompleted: false,
    studentCount: '',
    teacherCount: '',
    curriculums: [],
    subjects: [],
  }
  academyAdditionalData: res.Academy['academyAdditionalData'] = {
    salesManager: '',
    authPhoneNumber: '',
    authName: '',
    authBirthday: '',
    sampleSignatureWorkbookShowFlag: true,
    contractContactRequestedFlag: false,
    isSeoltab: false,
    examAutoAssigning: [],
    isBetaTester: false,
    organizationDomainName: null,
    organizationName: null,
  }

  가상학생등록여부 = false
  subscribePlan: res.AcademyPayment['subscribePlan'] = null
  additionalPricingPlan?: res.Academy['additionalPricingPlan']
  authority: res.Academy['authority'] = 'BASIC'

  subscribeItems: res.Academy['subscribeItems'] = []
  studentLimitCount: res.Academy['studentLimitCount'] = 0
  readonly specialContractPlans: res.Academy['specialContractPlans'] = []
  signUpFrom: res.Academy['signUpFrom'] = null

  availableBetaFeatures: res.Academy['availableBetaFeatures'] = []
  languagePackFileUrl: res.Academy['languagePackFileUrl'] = null
  groupId: res.Academy['groupId'] = null
  academyGroupType: res.Academy['academyGroupType'] = null

  constructor(data?: res.Academy) {
    super()

    makeObservable<this>(this, {
      status: observable,
      name: observable,
      email: observable,
      logoUrl: observable,
      initialized: observable,
      initializedDate: observable,
      cellPhone: observable,
      sellerCode: observable,
      freeStartDate: observable,
      freeEndDate: observable,
      onlineOnboardingStartedDate: observable,
      onlineOnboardingEndedDate: observable,
      payDate: observable,
      payStartDate: observable,
      payEndDate: observable,
      monthlyBaseFee: observable,
      customerType: observable,
      logoOperation: observable,
      logoUrlDateNow: observable,
      worksheetShareSetting: observable,
      supportedWorkbookTypes: observable,
      studentCount: observable,
      lessonClassCount: observable,
      가상학생등록여부: observable,
      metadata: observable,
      academyId: observable,
      academyAdditionalData: observable,
      logoUrlAdjusted: computed,
      payStatus: computed,
      isNoneLogoUrl: computed,
      isSales: computed,
      isFreeInProgressSales: computed,
      isFreeInProgress: computed,
      needCustomerTypeChange: computed,
      setCustomerType: action,
      subscribePlan: observable,
      additionalPricingPlan: observable,
      authority: observable,
      subscribeItems: observable,
      studentLimitCount: observable,
      specialContractPlans: observable,
      signUpFrom: observable,
      availableBetaFeatures: observable,
      groupId: observable,
      academyGroupType: observable,
      isCreverse: computed,
      serviceSubscriptions: observable,
    })

    if (data) {
      const academyAdditionalData = data.academyAdditionalData ?? {}
      const academyData = {
        ...data,
        _subscribeItems: data.subscribeItems,
        metadata: {
          ...this.metadata,
          ...data.metadata,
          lectureService: data.metadata.lectureService,
          smsService: data.metadata.smsService,
          attendanceSmsService: data.metadata.attendanceSmsService,
          thirdPartyProvisionConsentOnPageShop: data.metadata.thirdPartyProvisionConsentOnPageShop,
          freePremiumAvailable: data.metadata.freePremiumAvailable,
          studentCount: data.metadata.studentCount,
          teacherCount: data.metadata.teacherCount,
          curriculums: data.metadata.curriculums,
          subjects: data.metadata.subjects,
        },
        academyAdditionalData: {
          ...this.academyAdditionalData,
          ...academyAdditionalData,
        },
        serviceSubscriptions: data.serviceSubscriptions,
      }

      if (commonRepo.isSchoolflat) {
        const DEFAULT_MATHFLAT_LOGO_URL =
          'https://mathflat-user-uploads-dev.mathflat.com/logo/Default.png'
        const DEFAULT_SCHOOLFLAT_LOGO_URL =
          'https://mathflat-frontend-asset.s3.ap-northeast-2.amazonaws.com/teacher/assets/schoolflat_logo.png'
        if (data.logoUrl === DEFAULT_MATHFLAT_LOGO_URL) {
          academyData.logoUrl = DEFAULT_SCHOOLFLAT_LOGO_URL
        }
      }

      this.update<Academy>({
        ...academyData,
        initializedDate: academyData.initializedDate ? new Date(academyData.initializedDate) : null,
      })
    }
  }

  get payStatus() {
    switch (this.status) {
      case 'PAY_INPROGRESS':
        return '정식가입'
      case 'PAY_SCHEDULED':
        return '정식가입 예정'
      case 'FREE_INPROGRESS':
      default:
        return '무료체험'
    }
  }

  get dashedPhone() {
    if (!this.cellPhone) return '-'

    const number = this.cellPhone.match(/\d+/g)?.join('')
    return number ? stringUtils.toDashedPhone(number) : this.cellPhone
  }

  // 이미지 업데이트 후에 캐싱된 값 바라보지 않도록 logoUrl뒤에 난수 추가. 업데이트 하지 않을 때는 캐시 유지해야하므로 조건부로 붙혀줌.
  get logoUrlAdjusted() {
    return `${this.logoUrl}${this.logoUrlDateNow ? `?${this.logoUrlDateNow}` : ''}`
  }

  get isNoneLogoUrl() {
    return this.logoUrl.includes('None.png')
  }

  get isSales() {
    return this.sellerCode && this.sellerCode.startsWith('SYS')
  }

  get isFreeInProgressSales() {
    return this.isFreeInProgress && this.isSales
  }

  get isB2G() {
    return this.customerType?.includes('B2G')
  }

  get isFranchise() {
    return this.customerType === 'B2B-프랜차이즈'
  }

  get isCreverse() {
    return this.academyGroupType === 'CREVERSE'
  }

  get isFreeInProgress() {
    return this.status === 'FREE_INPROGRESS'
  }

  get isScienceflatAllowed() {
    return this.serviceSubscriptions.includes('SCIENCEFLAT')
  }

  get needCustomerTypeChange() {
    const targetTypes = ['전화 분류 필요', '분류 필요']

    return (
      this.status === 'PAY_INPROGRESS' &&
      (!this.customerType || targetTypes.includes(this.customerType))
    )
  }

  get subscriptionPlanType() {
    switch (this.subscribePlan) {
      case 'PREMIUM':
      case 'PREMIUM_PLUS':
      case 'PREMIUM_CUSTOM':
      case 'PREMIUM_UNLIMITED':
      case 'PREMIUM_STUDENT_CONTRACT':
        return '프리미엄요금제' // 프리미엄 요금제는 항상 정식가입/정식예정이며 authority도 PREMIUM 이다.
      default:
        return '베이직요금제'
    }
  }

  get isPremiumPlan() {
    return this.subscriptionPlanType === '프리미엄요금제'
  }

  get isStudentContractPlan() {
    return this.subscribePlan === 'PREMIUM_STUDENT_CONTRACT'
  }

  get isPayStartDateIn90Days() {
    if (!this.payStartDate) return false

    return isBefore(new Date(), addDays(new Date(this.payStartDate), 90))
  }

  // 시작일·종료일 당일 포함. Date 파싱 시 종료일이 자정 기준이 되어 당일 대부분이 제외되는
  // 문제를 피하려고 yyyy-MM-dd 문자열 비교로 판단한다.
  // computed 로 등록하지 않는다 — new Date() 는 observable 이 아니라 computed 로 두면 첫 평가
  // 시점의 today 가 메모이즈돼, SPA 를 켜둔 채 기간 경계를 넘어도 판정이 갱신되지 않는다.
  // 일반 getter 로 두어 접근(렌더)마다 현재 시각으로 재평가되게 한다.
  get isWithinOnlineOnboardingPeriod() {
    if (!this.onlineOnboardingStartedDate || !this.onlineOnboardingEndedDate) return false

    const today = format(new Date(), 'yyyy-MM-dd')
    return this.onlineOnboardingStartedDate <= today && today <= this.onlineOnboardingEndedDate
  }

  setCustomerType(type: res.Academy['customerType']) {
    this.customerType = type
  }
}
